import React, { useEffect, useState, useMemo, useContext } from "react";
import { useSnackbar } from "notistack";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from '@mui/icons-material/Visibility';
import httpRequest from "../../httpRequest";
import ButtonContained from "../StyledComponents/ButtonContained";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";
import { Card, CardHeader, Bar, Dialog, AnalyticalTable } from '@ui5/webcomponents-react';
import UserContext from "../../context/user-context"; // Import UserContext

const InvoiceTable = ({ invoiceData, openSummary, refreshInvoiceData, loading }) => {
  const { enqueueSnackbar } = useSnackbar();
  const userCtx = useContext(UserContext); // Access UserContext
  const [isSyncing, setIsSyncing] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [sortModel, setSortModel] = useState([{ field: 'id', sort: 'desc' }]);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/process_mailboxes`);
      enqueueSnackbar("Invoices synced successfully", { variant: "success" });
      refreshInvoiceData();
    } catch (error) {
      console.error("Error processing mailboxes:", error);
      enqueueSnackbar("Error processing mailboxes", { variant: "error" });
    }
    setIsSyncing(false);
  };

  const getStatusMessage = (invoice) => {
    const status = invoice.status;
    const currentUserEmployeeCode = userCtx?.employeeCode; // Access employee code from context
    const currentUserId = userCtx?.userId; // Access user ID from context
    let message = "";
    let highlightColor = "None"; // Default highlight color
  
    // Check if the invoice is rejected
    if (invoice.is_rejected) {
      return { message: "Rejected", highlightColor: "Negative" };
    }
  
    if (!status || !currentUserEmployeeCode || !currentUserId) {
      return { message: status, highlightColor };
    }
  
    if (status === "Pending") {
      if (!invoice.standing_employee_code) {
        message = "Action Required";
        highlightColor = "Critical";
      } else {
        message = "Pending"; // No special action, just Pending
        highlightColor = "Neutral";
      }
    } else if (status === "Initiated") {
      if (invoice.standing_employee_code === currentUserEmployeeCode) {
        message = "Action Required";
        highlightColor = "Critical";
      } else if (!invoice.standing_employee_code) {
        message = "Pending"; // If no standing employee code, still show Pending
        highlightColor = "Neutral";
      } else {
        // Check if current user ID is present in the approval_flow array
        const approvalFlow = invoice.approval_flow || [];
        const userInApprovalFlow = approvalFlow.some((flow) => flow.includes(currentUserId));
        
        if (userInApprovalFlow) {
          message = "Released";
          highlightColor = "Information";
        } else {
          message = "Initiated"; // Default case when nothing matches
          highlightColor = "Information";
        }
      }
    } else if (status === "Approved") {
      message = "Approved";
      highlightColor = "Positive";
    } else {
      message = status; // Fallback for unexpected statuses
      highlightColor = "None";
    }
  
    return { message, highlightColor };
  };
  
  const rows = invoiceData
    .filter((invoice) => {
      // Check if parsed_data exists and can be parsed
    if (invoice.parsed_data) {
      try {
        const parsedData = JSON.parse(invoice.parsed_data);
        return true; // Always include the invoice if parsed successfully
      } catch (error) {
        console.error("Error parsing parsed_data:", error);
        return false; // Exclude invoices that can't be parsed
      }
    }
    return false; // Exclude if parsed_data does not exist
    })
    .map((invoice) => {
      let invNumber = 'Unrecognized';
      let amount = '';
      let currency = '';
  
      if (invoice.parsed_data) {
        const parsedData = JSON.parse(invoice.parsed_data);
  
        // Use Document Number or a placeholder if it is empty
        invNumber = parsedData["Document Number"] || "N/A";
  
        amount = parsedData["Invoice Amount"] || "N/A";
        currency = parsedData["Currency"] || "N/A";
  
        if (amount) {
          const cleanedAmount = amount.replace(/[^\d.-]/g, '');
          const numericAmount = parseFloat(cleanedAmount);
          amount = numericAmount.toLocaleString();
        }
      }

      const { message: statusMessage, highlightColor } = getStatusMessage(invoice);

      return {
        id: invoice.id,
        inv_number: invNumber,
        vendor_name: invoice.vendor_name,
        amount: amount,
        currency: currency,
        company_name: invoice.company_name,
        function_name: invoice.function_name,
        status: statusMessage,
        highlight: highlightColor,
      };
    });

  const openInvoiceSummary = (invoiceId) => {
    const invoiceDataSelected = invoiceData.find((invoice) => invoice.id === invoiceId);
    openSummary(invoiceDataSelected);
  };

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const sortField = sortModel[0]?.field || 'id'; // Default to 'id'
      const direction = sortModel[0]?.sort === 'asc' ? 1 : -1;

      return (a[sortField] > b[sortField] ? 1 : -1) * direction;
    });
  }, [rows, sortModel]);

  const handleDeleteConfirmation = async () => {
    try {
      await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/delete-invoice`, {
        params: { id: selectedInvoiceId },
      });
      enqueueSnackbar("Invoice deleted successfully!", { variant: "success" });
      refreshInvoiceData();
      setSelectedInvoiceId(null);
    } catch (error) {
      console.error("Error while deleting invoice:", error);
      enqueueSnackbar("Error while deleting invoice", { variant: "error" });
    }
    setOpenDeleteDialog(false);
  };

  const openDeleteDialogHandler = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setOpenDeleteDialog(true);
  };

  const columns = [
    { Header: "Scan", accessor: "id", width: "50", autoResizable: true },
    { Header: "Document Number", accessor: "inv_number", width: "200", autoResizable: true  },
    { Header: "Vendor Name", accessor: "vendor_name", width: "170", autoResizable: true },
    { Header: "Invoice Amount", accessor: "amount", hAlign: "End", width: "150", autoResizable: true },
    { Header: "Currency", accessor: "currency", width: "100", autoResizable: true },
    { Header: "Company", accessor: "company_name", width: "200", autoResizable: true },
    { Header: "Function", accessor: "function_name", width: "170", autoResizable: true  },
    {
      Header: "Status",
      accessor: "status",
      autoResizable: true,
      Cell: ({ cell }) => {
        const highlight = cell.row.original.highlight;

        // Map the highlight term to actual CSS colors
        const colorMap = {
          "Information": "#0070f2",    
          "Critical": "#e76500", 
          "Negative": "#aa0808",
          "Positive": "#256f3a",
          "Neutral": "#788fa6",
          "None": "#788fa6"
        };

        const validColor = colorMap[highlight] || "black"; // Fallback to black if no match

        return (
          <span style={{ color: validColor }}>{cell.value}</span>
        );
      },
    },
    {
      Header: "Actions",
      width: "100",
      accessor: "actions",
      disableFilters: true,
      disableGroupBy: true,
      disableResizing: true,
      disableSortBy: true,
      Cell: ({ row }) => (
        <>
          <ButtonOutlined onClick={() => openInvoiceSummary(row.original.id)}>
            <VisibilityIcon />
          </ButtonOutlined>
          {userCtx?.overriddenRole.can_delete_invoice && ( // Use userCtx to check permission
<ButtonOutlined
    onClick={() => openDeleteDialogHandler(row.original.id)}
    {...(!["Pending", "Action Required", "Rejected"].includes(row.original.status) ? { disabled: true } : {})}
>              <DeleteIcon />
            </ButtonOutlined>
          )}
        </>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
      <Card
        header={
          <CardHeader
            subtitleText="Review changes, release, and track invoices"
            titleText="Invoice Inbox"
            additionalText={isSyncing ? "Syncing..." : "Sync Now"} // Pass the text directly
            interactive
            onClick={!isSyncing ? handleSync : undefined} // Make CardHeader clickable
          />
        }
        style={{ width: "92%", overflow: "hidden" }}
      >
        <AnalyticalTable
          data={sortedRows}
          columns={columns}
          sortable
          filterable
          selectionMode="None"
          withNavigationHighlight
          withRowHighlight
          highlightField="highlight"
          scaleWidthMode="Smart"
          loading={loading}
          sortBy={sortModel} // Ensure to use your dynamic sortModel
          onSort={(e) => {
            const { column, sortDirection } = e.detail;
            setSortModel([{ field: column.id, sort: sortDirection }]);
          }}
          style={{ padding: '20px' }}
        />

        <Dialog
          headerText={`Confirm Delete`}
          open={openDeleteDialog}
          onAfterClose={() => setOpenDeleteDialog(false)}
          state="Critical"
          footer={
            <Bar
              design="Footer"
              endContent={
                <>
                  <ButtonOutlined style={{ marginRight: 8 }} onClick={() => setOpenDeleteDialog(false)}>
                    Cancel
                  </ButtonOutlined>
                  <ButtonContained onClick={handleDeleteConfirmation}>Yes</ButtonContained>
                </>
              }
            />
          }
        >
          Are you sure you want to delete the selected invoice?
        </Dialog>
      </Card>
    </div>
  );
};

export default InvoiceTable;
